import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import remoteApi from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtime } from "../contexts/RealtimeContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Send,
  User,
  ShieldCheck,
  MoreVertical
} from "lucide-react";

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lastEvent } = useRealtime();
  const { t } = useTranslation();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const messageListRef = useRef(null);

  const getStatusColor = (ticketStatus) => {
    switch (ticketStatus?.toLowerCase()) {
      case "open":
        return {
          bg: "bg-blue-100 text-blue-700 border-blue-200",
          icon: <AlertCircle className="w-3.5 h-3.5" />,
          label: t("ticket.status.open"),
        };
      case "in_progress":
      case "in-progress":
        return {
          bg: "bg-amber-100 text-amber-700 border-amber-200",
          icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
          label: t("ticket.status.in_progress"),
        };
      case "resolved":
        return {
          bg: "bg-green-100 text-green-700 border-green-200",
          icon: <CheckCircle2 className="w-3.5 h-3.5" />,
          label: t("ticket.status.resolved"),
        };
      case "closed":
        return {
          bg: "bg-gray-100 text-gray-600 border-gray-200",
          icon: <XCircle className="w-3.5 h-3.5" />,
          label: t("ticket.status.closed"),
        };
      default:
        return {
          bg: "bg-gray-100 text-gray-600 border-gray-200",
          icon: <MessageCircle className="w-3.5 h-3.5" />,
          label: ticketStatus || t("ticket.unknown"),
        };
    }
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await remoteApi.getTicket(ticketId);
        const payload = res.data || res;
        setTicket(payload.ticket || payload);
        setMessages(payload.messages || payload.messages || []);
      } catch (err) {
        console.error("Failed to load ticket", err?.message || err);
      } finally {
        setLoading(false);
      }
    }
    if (ticketId) load();
  }, [ticketId]);

  useEffect(() => {
    if (
      lastEvent &&
      lastEvent.eventType === "ticket_message" &&
      lastEvent.ticketId === ticketId
    ) {
      setMessages((prev) => [...prev, lastEvent.payload.message]);
    }
    if (
      lastEvent &&
      lastEvent.eventType === "ticket_update" &&
      lastEvent.payload?.ticket?.ticket_id === ticketId
    ) {
      setTicket(lastEvent.payload.ticket);
    }
  }, [lastEvent, ticketId]);

  // Scroll to bottom
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  async function handleSend() {
    if (!message.trim()) return;
    try {
      const msgContent = message;
      setMessage("");
      await remoteApi.postTicketMessage(ticketId, msgContent);

      const now = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        {
          message_id: `local-${now}`,
          ticket_id: ticketId,
          from: "user",
          userId: user.user_id || user.id,
          content: msgContent,
          createdAt: now,
        },
      ]);
    } catch (err) {
      console.error("Failed to send message", err?.message || err);
    }
  }

  const statusInfo = getStatusColor(ticket?.status);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!loading && !ticket) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
        <div className="p-4 bg-red-50 rounded-full mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t("uiFixes.ticket_not_found")}</h2>
        <p className="text-gray-500 mb-6">{t("uiFixes.ticket_not_found_desc")}</p>
        <button onClick={() => navigate("/tickets")} className="px-6 py-2 bg-gray-900 text-white rounded-xl font-bold">{t("uiFixes.go_back")}</button>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto p-4 md:p-6 lg:h-[calc(100vh-2rem)] flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 flex-shrink-0">
        <button
          onClick={() => navigate("/tickets")}
          className="p-2.5 bg-white hover:bg-gray-50 text-gray-600 rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">
              #{ticketId?.slice(0, 8)}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusInfo.bg}`}>
              {statusInfo.icon} {statusInfo.label}
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 truncate">
            {ticket.subject}
          </h1>
        </div>

        <div className="text-right hidden sm:block">
          <div className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">{t("uiFixes.last_updated")}</div>
          <div className="text-sm font-bold text-gray-700">
            {new Date(ticket.updatedAt).toLocaleDateString(i18n.language)}
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex flex-col relative">

        {/* Messages Area */}
        <div
          ref={messageListRef}
          className="flex-1 overflow-y-auto p-6 space-y-6 scroll-smooth bg-gray-50/50"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-60">
              <MessageCircle className="w-16 h-16 mb-4" />
              <p className="font-medium">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((m, i) => {
              const isAdmin = m.from === "admin" || m.adminId;
              const isMe = !isAdmin;

              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={m.message_id || i}
                  className={`flex gap-3 ${isMe ? "justify-end" : "justify-start"}`}
                >
                  {!isMe && (
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0 mt-2 shadow-sm border border-primary-200">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                  )}

                  <div className={`max-w-[80%] md:max-w-[60%] space-y-1 ${isMe ? "items-end flex flex-col" : "items-start flex flex-col"}`}>
                    <div className={`
                                px-5 py-3.5 rounded-2xl shadow-sm text-sm leading-relaxed
                                ${isMe
                        ? "bg-primary-600 text-white rounded-tr-sm"
                        : "bg-white text-gray-800 border border-gray-100 rounded-tl-sm font-medium"}
                             `}>
                      {m.content}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium px-1">
                      <span>
                        {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isAdmin && (
                        <span className="bg-primary-50 text-primary-600 px-1.5 py-0.5 rounded-md">{t("uiFixes.support_team")}</span>
                      )}
                    </div>
                  </div>

                  {isMe && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0 mt-2 shadow-sm">
                      <User className="w-4 h-4" />
                    </div>
                  )}
                </motion.div>
              )
            })
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-100 z-10">
          <div className="relative flex items-end gap-3 max-w-4xl mx-auto">
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-3xl focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all shadow-inner">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={t("uiFixes.type_message")}
                className="w-full bg-transparent border-none px-5 py-4 focus:ring-0 max-h-32 min-h-[56px] resize-none text-gray-800 placeholder-gray-400"
                rows={1}
                style={{ minHeight: "56px" }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!message.trim()}
              className="p-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg shadow-primary-600/30 hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:shadow-none disabled:transform-none flex-shrink-0"
            >
              <Send className="w-5 h-5 ml-0.5" />
            </button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">
            Press <span className="font-bold">Enter</span> to send, <span className="font-bold">Shift + Enter</span> for new line
          </p>
        </div>
      </div>
    </motion.div>
  );
}
